
import React from 'react';
import { Undo2, X, Check } from 'lucide-react';
import type { DrawingToolType } from '../../hooks/useDrawingMode';
import { useTranslation } from 'react-i18next';

interface DrawingToolbarProps {
  toolType: DrawingToolType;
  pointCount: number;
  formattedDistance: string;
  formattedArea: string;
  canFinish: boolean;
  onUndo: () => void;
  onFinish: () => void;
  onCancel: () => void;
}

const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  toolType, pointCount, formattedDistance, formattedArea, canFinish,
  onUndo, onFinish, onCancel,
}) => {
  const { t } = useTranslation();
  const measurement = toolType === 'area' ? formattedArea : formattedDistance;
  const typeLabel = toolType === 'line' ? 'linje' : 'omrade';

  return (
    <>
      {/* Top instruction bar */}
      {pointCount === 0 && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-mapOverlay pointer-events-none">
          <div className="bg-[var(--bg-surface)]/90 backdrop-blur-xl border border-emerald-500/30 rounded-2xl px-4 py-2 shadow-2xl">
            <p className="text-tiny font-bold text-[var(--text-primary)] whitespace-nowrap">
              {t("auto.map.drawingtoolbar.trykk_pa_kartet_for_a_plassere", "Trykk pa kartet for a plassere punkter")}
            </p>
          </div>
        </div>
      )}

      {/* Bottom action bar */}
      <div className="absolute bottom-24 left-4 right-4 md:left-auto md:right-auto md:left-1/2 md:-translate-x-1/2 md:w-auto z-mapOverlay pointer-events-auto">
        <div className="flex items-center gap-2 bg-[var(--bg-surface)]/90 backdrop-blur-xl border border-emerald-500/30 rounded-2xl px-3 py-2 shadow-2xl">
          {/* Undo */}
          <button
            onClick={onUndo}
            disabled={pointCount === 0}
            className="p-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            title={t("auto.map.drawingtoolbar.title.angre", "Angre")}
          >
            <Undo2 size={16} />
          </button>

          {/* Stats */}
          <div className="flex items-center gap-2 px-3">
            <span className="text-tiny font-black text-[var(--text-primary)]">
              {pointCount} {pointCount === 1 ? 'punkt' : 'punkter'}
            </span>
            {pointCount >= 2 && (
              <>
                <span className="text-[var(--text-muted)]">·</span>
                <span className="text-tiny font-bold text-emerald-400">{measurement}</span>
              </>
            )}
          </div>

          {/* Finish */}
          <button
            onClick={onFinish}
            disabled={!canFinish}
            className="flex items-center gap-1 px-4 py-2 bg-emerald-500 text-white rounded-xl text-tiny font-bold disabled:opacity-30 hover:bg-emerald-600 active:scale-95 transition-all"
          >
            <Check size={14} /> {t("auto.map.drawingtoolbar.ferdig", "Ferdig")}
          </button>

          {/* Cancel */}
          <button
            onClick={onCancel}
            className="p-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors"
            title={t("auto.map.drawingtoolbar.title.avbryt", "Avbryt")}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default DrawingToolbar;
